import { api } from '$lib/utils/api';
import {
  createMutation,
  createQuery,
  keepPreviousData,
} from '@tanstack/svelte-query';

export const useCreateBookingBlockMutation = (options = {}) =>
  createMutation({
    mutationKey: ['bookingBlock'],
    mutationFn: (bookingBlock) =>
      api().post('nurse/calendar/bookingBlock', bookingBlock),
    ...options,
  });

export const useCreateEventsMutation = (options = {}) =>
  createMutation({
    mutationKey: ['events'],
    mutationFn: ({ events }) => api().post('nurse/calendar/events', { events }),
    ...options,
  });

export const createEvent = (data) =>
  api().post(`nurse/calendar/event`, {
    ...data,
    nurseComment: data.internalComment,
  });

export const updateCalenderEvent = ({
  clientEmail,
  companyId,
  description,
  eventType,
  language,
  userId,
  topicId,
}) =>
  api().put(`nurse/clients/calender/event/${companyId}`, {
    clientEmail,
    companyId,
    description,
    eventType,
    language,
    userId,
    topicId,
  });

/**
 * Transform V3 normalized response to V2 shape for backwards compatibility.
 * V3 is ~90% smaller payload but frontend expects V2 structure.
 */
function transformV3toV2(v3Data) {
  const { events, bookingBlocks } = v3Data;

  // Build lookup maps
  const blockById = new Map(bookingBlocks.map((b) => [b.id, b]));

  // Group events by bookingBlockId
  const eventsByBlockId = new Map();
  for (const event of events) {
    if (event.bookingBlockId) {
      if (!eventsByBlockId.has(event.bookingBlockId)) {
        eventsByBlockId.set(event.bookingBlockId, []);
      }
      eventsByBlockId.get(event.bookingBlockId).push(event);
    }
  }

  // Transform booking blocks - add embedded events and calculate bookableEvents
  const transformedBlocks = bookingBlocks.map((block) => {
    const blockEvents = eventsByBlockId.get(block.id) || [];

    // Calculate bookable slots (same logic as V2 server-side)
    const bookableEvents = calculateBookableSlots(block, blockEvents);

    return {
      ...block,
      events: blockEvents,
      bookableEvents,
    };
  });

  // Transform events - add embedded bookingBlock for events that have one
  const transformedEvents = events.map((event) => {
    if (event.bookingBlockId) {
      const block = blockById.get(event.bookingBlockId);
      return {
        ...event,
        bookingBlock: block || null,
      };
    }
    return event;
  });

  return {
    events: transformedEvents,
    bookingBlocks: transformedBlocks,
    // Flatten all bookableEvents for top-level access (used by some modals)
    bookableEvents: transformedBlocks.flatMap((b) => b.bookableEvents || []),
  };
}

/**
 * Calculate bookable time slots for a booking block.
 * Generates slots based on block duration and filters out already booked times and past slots.
 */
function calculateBookableSlots(block, bookedEvents) {
  const slots = [];
  const slotDuration = block.slotsDuration || 30; // minutes
  const slotsGap = block.slotsGap || 0; // minutes between slots
  const blockStart = new Date(block.start);
  const blockEnd = new Date(block.end);
  const now = Date.now();

  // Get booked time ranges (excluding cancelled and breaks shown separately)
  const bookedRanges = bookedEvents
    .filter((e) => e.status !== 'CANCELED')
    .map((e) => ({
      start: new Date(e.start).getTime(),
      end: new Date(e.end).getTime(),
      isBreak: e.eventType === 'BLOCKED',
    }));

  let currentTime = blockStart.getTime();
  const endTime = blockEnd.getTime();
  let slotIndex = 0;

  while (currentTime + slotDuration * 60000 <= endTime) {
    const slotStart = currentTime;
    const slotEnd = currentTime + slotDuration * 60000;

    // Skip past slots - they can't be booked
    const isPast = slotStart < now;
    if (isPast) {
      currentTime = slotEnd + slotsGap * 60000;
      slotIndex++;
      continue;
    }

    // Check if this slot overlaps with any booked event
    const overlappingBooking = bookedRanges.find(
      (range) => slotStart < range.end && slotEnd > range.start,
    );

    if (!overlappingBooking) {
      slots.push({
        start: new Date(slotStart).toISOString(),
        end: new Date(slotEnd).toISOString(),
        eventType: block.eventType,
        companyId: block.companyId,
        companyName: block.companyName,
        userId: block.userId,
        bookingBlockId: block.id,
        location: block.location,
        isRest: false,
        slotIndex,
      });
    }

    currentTime = slotEnd + slotsGap * 60000; // Add gap between slots
    slotIndex++;
  }

  return slots;
}

/**
 * Options only — for callers whose date range changes while the component stays
 * mounted. createQuery reads the query-client context, so it throws if it is
 * rebuilt from a $derived; pass these through `toStore(() => ...)` instead.
 */
export const calendarEventsOptions = (startDate, endDate) => ({
  queryKey: ['calendarV3', startDate.toString(), endDate.toString()],
  queryFn: async () => {
    const v3Data = await api().get('v3/calendar/backOfficeCalenderViewEvents', {
      start: startDate,
      end: endDate,
    });
    return transformV3toV2(v3Data);
  },
  refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  // v5 replaced the keepPreviousData flag with placeholderData
  placeholderData: keepPreviousData,
  staleTime: 1000 * 10, // 10 seconds
  gcTime: 1000 * 10, // 10 seconds
});

export const useGetCalendarEvents = (startDate, endDate) =>
  createQuery(calendarEventsOptions(startDate, endDate));

export const useGetAvailableSlots = ({ eventId } = {}, options = {}) =>
  createQuery({
    queryKey: ['availableSlots', eventId],
    queryFn: () => api().get(`v2/calendar/availableSlots`, { eventId }),
    ...options,
  });

export const getEvent = (id) => api().get(`nurse/calendar/event/${id}`);

export const useGetEventQuery = (id) =>
  createQuery({
    queryKey: ['event', id],
    queryFn: () => api().get(`nurse/calendar/event/${id}`),
  });

export const updateEventTime = (eventId, start, end) =>
  api().put(`nurse/calendar/event/${eventId}`, {
    start,
    end,
  });

export const getTopics = () =>
  createQuery({
    queryKey: ['topics'],
    queryFn: () => api().get(`nurse/calendar/topics`),
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

export const getCalendarEventStatuses = () =>
  createQuery({
    queryKey: ['statuses'],
    queryFn: () => api().get(`nurse/calendar/statuses`),
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

export const getMeetingTypes = () =>
  createQuery({
    queryKey: ['types'],
    queryFn: () => api().get(`nurse/calendar/types`),
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

export const useGetNursesQuery = (props, options = {}) =>
  createQuery({
    // `props` belongs in the key: it is passed to the request, and without it
    // this shares a cache entry with getNurses() below, which fetches the
    // unfiltered list under the same key.
    queryKey: ['nurses', props],
    queryFn: () => api().get(`v2/user/active`, props),
    ...options,
  });

export const getNurses = () =>
  createQuery({
    queryKey: ['nurses'],
    queryFn: () => api().get(`v2/user/active`),
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

export const getPlaces = () =>
  createQuery({
    queryKey: ['places'],
    queryFn: () => api().get(`v2/calendar/places`),
    gcTime: 1000 * 60 * 60 * 2, // 2 hours
    staleTime: 1000 * 60 * 60 * 2, // 2 hours
  });

export const getPendingEmailsQuery = () =>
  createQuery({
    queryKey: ['pendingEmails'],
    queryFn: () => api().get('nurse/calendar/pendingEmails'),
  });

export const getBookingBlockPendingEmailsQuery = (bookingBlockId) =>
  createQuery({
    queryKey: ['bookingBlockPendingEmails', bookingBlockId],
    queryFn: () =>
      api().get(`v2/calendar/bookingBlockPendingEmails/${bookingBlockId}`),
  });

export const useDeliveryLocationRoomsQuery = (deliveryLocationId, companyId) =>
  createQuery({
    queryKey: ['deliveryLocationRooms', deliveryLocationId, companyId],
    queryFn: () =>
      api().get(
        `v2/calendar/deliveryLocations/${deliveryLocationId}/rooms?companyId=${companyId}`,
      ),
    enabled: !!deliveryLocationId && !!companyId,
    staleTime: 1000 * 60 * 5,
  });

// TODO: Remove and use mutation
export const updateEvent = (id, data) =>
  api().put(`nurse/calendar/event/${id}`, data);

export const useUpdateEventMutation = (options = {}) =>
  createMutation({
    mutationKey: ['updateEvent'],
    mutationFn: ({ id, data }) => api().put(`nurse/calendar/event/${id}`, data),
    ...options,
  });

export const useDeleteEventMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteEvent'],
    mutationFn: ({ id }) => api().delete(`v2/calendar/events/${id}`),
    ...options,
  });

export const useUpdateBookingBlockMutation = (options = {}) =>
  createMutation({
    mutationKey: ['updateBookingBlock'],
    mutationFn: ({ id, data }) =>
      api().put(`nurse/calendar/bookingBlock/${id}`, data),
    ...options,
  });

export const useDeleteBookingBlockMutation = (options = {}) =>
  createMutation({
    mutationKey: ['deleteBookingBlock'],
    mutationFn: ({ id }) =>
      api().delete(`nurse/calendar/cancel/bookingBlock/${id}`),
    ...options,
  });

export const sendPendingEventEmails = () =>
  api().post(`nurse/calendar/sendPendingEventEmails`, {
    companyId: 0,
  });

export const useSendPendingEmailsForBlockMutation = (options = {}) =>
  createMutation({
    mutationKey: ['sendPendingEventEmails'],
    mutationFn: ({ bookingBlockId }) =>
      api().post(`nurse/calendar/sendPendingEventEmails`, {
        bookingBlockId,
        allEmailTypes: true,
      }),
    ...options,
  });

export const sendNextEventEmail = (
  eventId,
  forceNewInvite = true,
  nextEmailType,
) =>
  api().post(`nurse/calendar/sendPendingEventEmail/${eventId}`, {
    force: forceNewInvite,
    emailType: nextEmailType,
  });

export const createBreak = ({
  bookingBlockId,
  companyId,
  language,
  start,
  end,
  userId,
}) =>
  api().post('nurse/calendar/event', {
    start,
    end,
    language,
    companyId,
    bookingBlockId,
    eventType: 'BLOCKED',
    userId,
  });

export const getTimeZones = () =>
  createQuery({
    queryKey: ['timeZones'],
    queryFn: () => api().get(`v2/calendar/timeZones`),
  });

export const useValidateBookingEmailsMutation = (options = {}) =>
  createMutation({
    mutationKey: ['validateBookingEmails'],
    mutationFn: ({ emails }) =>
      api().post('v2/calendar/validateBookingEmails', { emails }),
    ...options,
  });

export const useSendBookingLinksMutation = (options = {}) =>
  createMutation({
    mutationKey: ['sendBookingLinks'],
    mutationFn: ({
      clientIds,
      companyId,
      expiresAt,
      locationFilter,
      customMessage,
    }) =>
      api().post('v2/calendar/sendBookingLinks', {
        clientIds,
        companyId,
        expiresAt,
        locationFilter,
        customMessage,
      }),
    ...options,
  });

export const bookingOverviewOptions = (predicates, options = {}) => ({
  queryKey: ['bookingOverview', JSON.stringify(predicates)],
  queryFn: () => api().get('v2/calendar/bookingOverview', predicates),
  ...options,
});

export const useGetBookingOverviewQuery = (predicates, options = {}) =>
  createQuery(bookingOverviewOptions(predicates, options));

export const companyBookingBlocksOptions = (companyId, options = {}) => ({
  queryKey: ['companyBookingBlocks', companyId],
  queryFn: async () => {
    // Get booking blocks for the next 6 months
    const now = new Date();
    const endDate = new Date(now);
    endDate.setMonth(endDate.getMonth() + 6);

    const data = await api().get('v3/calendar/backOfficeCalenderViewEvents', {
      start: now.toISOString(),
      end: endDate.toISOString(),
    });

    // Filter to only HEALTH_CHECK blocks for this company
    return data.bookingBlocks
      .filter(
        (block) =>
          block.eventType === 'HEALTH_CHECK' && block.companyId === companyId,
      )
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  },
  enabled: !!companyId,
  ...options,
});

export const useGetCompanyBookingBlocksQuery = (companyId, options = {}) =>
  createQuery(companyBookingBlocksOptions(companyId, options));
